import { cn } from 'support/helpers/cn/cn';
import { DocumentTypeWithVirtual, ValueOf } from 'support/types';

import { SidebarSection } from '../../Sidebar/SidebarSection/SidebarSection';
import { useDocumentDisplayConfig } from '../DocumentDisplayConfig/DocumentDisplayConfigContext';
import { CollapsibleSummaryRow } from '../DocumentSummary/components/CollapsibleSummaryRow/CollapsibleSummaryRow';
import { DocumentSummary } from '../DocumentSummary/DocumentSummary';
import { filterSummaryByConfig } from '../DocumentSummary/filterSummaryByConfig';
import { hasSummary } from '../DocumentSummary/helpers';
import { Summary } from '../DocumentSummary/types';

export const SidebarDocumentSummary = ({
  documentType,
}: {
  documentType: ValueOf<DocumentTypeWithVirtual> | undefined;
}) => {
  if (!documentType && !hasSummary(documentType)) {
    return null;
  }

  return <DocumentSummary documentType={documentType} renderSummary={SummaryRenderer}></DocumentSummary>;
};

const SummaryRenderer = ({ summary }: { summary: Summary }) => {
  const config = useDocumentDisplayConfig();
  const filtered = filterSummaryByConfig(summary, config);

  if (filtered.content.length === 0 && !filtered.footer) {
    return null;
  }

  const hasSections = filtered.content.length > 0;

  return (
    <SidebarSection>
      <div>
        {filtered.content.map((items, index) => (
          <div
            key={index}
            className={cn('space-y-4', {
              'border-t border-gray-200 mt-3 pt-3': index > 0,
            })}
          >
            {items
              .filter((item) => (item.test ? item.test('view-document') : true))
              .map((item, itemIndex) => {
                const key = `${index}-${itemIndex}`;

                return item.subItems?.length ? (
                  <CollapsibleSummaryRow
                    key={key}
                    label={item.label}
                    value={item.value}
                    subItems={item.subItems}
                    className="flex items-center justify-between gap-4"
                    labelClassName="text-xs text-gray-500"
                    valueClassName="text-xs text-gray-900"
                    subItemsClassName="text-xs text-gray-500"
                  />
                ) : (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className="text-end text-xs text-gray-900">{item.value}</span>
                  </div>
                );
              })}
          </div>
        ))}
        {!!filtered.footer && (
          <div
            className={cn(
              { 'mt-5 border-t border-gray-200 pt-3': hasSections },
              '-mx-6 flex items-center justify-between gap-1 px-6',
            )}
          >
            <span className="text-xs text-gray-500">{filtered.footer.label}</span>
            <span className="text-end text-base font-bold text-gray-900 ">{filtered.footer.value}</span>
          </div>
        )}
      </div>
    </SidebarSection>
  );
};
